import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { crmAuth } from '@/services/auth/crm-auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Eye, EyeOff, Building2, FileCheck } from 'lucide-react';
import { CookieConsent } from '@/components/shared/CookieConsent';
import logoImg from '@/assets/img/logo.png';

export function CRMLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Password change state
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Force dark mode
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await crmAuth.signIn(email, password);

      // Check if password change is required
      if (result.requiresPasswordChange) {
        setRequiresPasswordChange(true);
        setIsLoading(false);
        return;
      }

      if (!result.success) {
        setError(result.error || 'Login failed. Please check your credentials.');
        setIsLoading(false);
        return;
      }

      await handleSuccessfulAuth();
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      setIsLoading(false);
      return;
    }

    try {
      // For CRM clients, use temp password as current password and set new one
      const result = await crmAuth.changePassword(password, newPassword);

      if (!result.success) {
        setError(result.error || 'Failed to update password');
        setIsLoading(false);
        return;
      }

      await handleSuccessfulAuth();
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessfulAuth = () => {
    // CRM auth is handled by crmAuth service (localStorage)
    // Navigate to the CRM dashboard
    navigate('/crm/dashboard');
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#282836]">
      {/* Left side - Login Form */}
      <div className="flex flex-col justify-center px-6 py-12 lg:px-12 xl:px-20">
        <div className="mx-auto w-full max-w-[400px] space-y-8">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img src={logoImg} alt="Logo" className="h-10 w-auto" />
          </div>

          {/* Header */}
          <div className="space-y-2">
            <h1 className="text-3xl font-bold tracking-tight text-white">
              {requiresPasswordChange ? 'Set Your Password' : 'Client Portal'}
            </h1>
            <p className="text-[15px] text-zinc-400">
              {requiresPasswordChange
                ? 'Please create a new password to secure your account'
                : 'Sign in to upload your documents and track your intake progress'
              }
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
              {error}
            </div>
          )}

          {!requiresPasswordChange ? (
            /* Login Form */
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-zinc-300">
                  Email address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  className="h-12 bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-primary focus:ring-primary/20"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-zinc-300">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="h-12 bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-primary focus:ring-primary/20 pr-12"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-12 w-12 text-zinc-400 hover:text-white hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-medium text-[15px] shadow-lg shadow-primary/20"
                disabled={isLoading}
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Sign in
              </Button>
            </form>
          ) : (
            /* Password Change Form */
            <form onSubmit={handlePasswordChange} className="space-y-5">
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-400">
                For security, please set a new password before continuing.
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-sm font-medium text-zinc-300">
                  New Password
                </Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="h-12 bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-primary focus:ring-primary/20 pr-12"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-12 w-12 text-zinc-400 hover:text-white hover:bg-transparent"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-zinc-500">
                  Must be at least 8 characters with uppercase, lowercase, and numbers
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-zinc-300">
                  Confirm Password
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  className="h-12 bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-primary focus:ring-primary/20"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-medium text-[15px] shadow-lg shadow-primary/20"
                disabled={isLoading || newPassword !== confirmPassword}
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Set Password & Continue
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full text-zinc-400 hover:text-white hover:bg-zinc-800"
                onClick={() => {
                  setRequiresPasswordChange(false);
                  setNewPassword('');
                  setConfirmPassword('');
                  setError('');
                }}
              >
                Back to login
              </Button>
            </form>
          )}

          {/* Forgot Password Link */}
          {!requiresPasswordChange && (
            <p className="text-center text-[13px] text-zinc-500">
              <Link to="/crm/forgot-password" className="text-primary hover:underline">
                Forgot your password?
              </Link>
            </p>
          )}

          {/* Help Text */}
          <p className="text-center text-[13px] text-zinc-500 leading-relaxed">
            Having trouble signing in?{' '}
            <a href="mailto:support@data-corner.com.au" className="text-primary hover:underline">
              Contact support
            </a>
          </p>

          {/* Legal Text */}
          <p className="text-center text-[13px] text-zinc-500 leading-relaxed">
            By signing in, you agree to our{' '}
            <Link to="/terms" className="text-primary hover:underline">
              Terms & Conditions
            </Link>{' '}
            and{' '}
            <Link to="/privacy-policy" className="text-primary hover:underline">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>

      {/* Right side - Branding */}
      <div className="hidden lg:flex flex-col relative overflow-hidden bg-gradient-to-br from-[#282836] via-[#0f0f18] to-[#282836]">
        {/* Gradient orbs */}
        <div className="absolute top-1/4 -right-20 w-96 h-96 bg-purple-600/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/3 -left-20 w-80 h-80 bg-blue-600/15 rounded-full blur-[100px]" />

        {/* Content */}
        <div className="flex flex-1 flex-col justify-center px-12">
          <div className="space-y-8">
            {/* Feature Cards */}
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/20">
                  <FileCheck className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Easy Document Upload</h3>
                  <p className="text-sm text-zinc-400">
                    Securely upload required documents with drag & drop simplicity
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-green-500/20">
                  <Building2 className="h-6 w-6 text-green-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Track Your Progress</h3>
                  <p className="text-sm text-zinc-400">
                    Monitor your intake status and see what's needed in real-time
                  </p>
                </div>
              </div>
            </div>

            {/* Headline */}
            <div className="space-y-4 mt-8">
              <h2 className="text-4xl font-bold text-white leading-tight">
                Complete Your<br />
                Client Onboarding<br />
                <span className="text-primary">In Minutes</span>
              </h2>
              <p className="text-zinc-400 text-lg max-w-md">
                Upload documents, track your progress, and get answers instantly with our AI-powered support.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom decoration */}
        <div className="h-32 bg-gradient-to-t from-primary/5 to-transparent" />
      </div>

      {/* Cookie Consent */}
      <CookieConsent />
    </div>
  );
}
